/**
 * E4. Блэкджек, упрощённый: игрок против дилера, «ещё/хватит», без сплитов
 * и удвоений. Дилер берёт карты до 17 (и на мягких 17 стоит). Натуральный
 * блэкджек (21 с двух карт, у дилера нет 21) — ×2,5 от ставки. Ничья — возврат.
 * Колода — 52 карты, перемешана `Rng` в setup; дальше раздача детерминирована
 * порядком колоды, поэтому `blackjackHit` не нуждается в Rng.
 *
 * `play` с `hit` берёт одну карту: перебор — проигрыш, иначе дилер играет
 * и рука сравнивается (то есть «ещё, затем хватит»). Для нескольких «ещё»
 * клиент продвигает состояние через `blackjackHit` и завершает `play(stand)`.
 *
 * Без ставки (денег нет) рука не раздаётся и не засчитывается — `noPlay()`.
 * Тяга +3; знания +1 за первые BLACKJACK_LEARN_HANDS рук (`flags.bjHands`).
 *
 * Ожидание на столе считается по этим правилам, а не берётся «из воздуха»:
 * наивная стратегия (`blackjackNaiveAction`: брать, пока меньше 17, как дилер)
 * возвращает BLACKJACK_RTP_NAIVE, базовая (`blackjackBasicAction`: с учётом
 * открытой карты дилера, без удвоений и сплитов) — BLACKJACK_RTP_BASIC.
 * Оба числа получены симуляцией N = 200 000 рук на этих правилах и
 * сверяются тестом; подсказка базовой стратегии открывается после
 * BLACKJACK_LEARN_HANDS рук. Спека E4 называла −2 % / −0,5 %, но такие
 * числа требуют удвоений/сплитов, которых здесь нет.
 */
import type { Dataset, GamblingLaw } from '@life/data';
import type { Life, MinigameOutcome, Rng } from '@life/engine';
import type { MinigameDef } from '../types';
import { casinoAvailable, clampBet, expectancy, noPlay } from './common';

export type Card = 'A' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9' | '10' | 'J' | 'Q' | 'K';

export const CARD_RANKS: readonly Card[] = ['A', '2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K'];
export const BLACKJACK_NATURAL_PAYOUT = 2.5;
export const BLACKJACK_DEALER_STANDS = 17;
export const BLACKJACK_LEARN_HANDS = 20;
export const BLACKJACK_URGE = 3;
export const BLACKJACK_RTP_NAIVE = 0.94;
export const BLACKJACK_RTP_BASIC = 0.98;
export const FLAG_BJ_HANDS = 'bjHands';

export interface BlackjackState {
  deck: Card[];
  hand: Card[];
  dealer: Card[];
  maxBet: number;
  /** Сыгранных рук до этой (из `flags.bjHands`). */
  hands: number;
}

export interface BlackjackInput {
  bet: number;
  action: 'hit' | 'stand';
}

export function handValue(hand: readonly Card[]): number {
  let total = 0;
  let aces = 0;
  for (const c of hand) {
    if (c === 'A') {
      aces += 1;
      total += 11;
    } else if (c === 'K' || c === 'Q' || c === 'J' || c === '10') total += 10;
    else total += Number(c);
  }
  while (total > 21 && aces > 0) {
    total -= 10;
    aces -= 1;
  }
  return total;
}

export function isNatural(hand: readonly Card[]): boolean {
  return hand.length === 2 && handValue(hand) === 21;
}

/** Перемешанная колода из 52 карт (Фишер–Йетс на Rng). */
export function shuffledDeck(rng: Rng): Card[] {
  const deck: Card[] = [];
  for (let suit = 0; suit < 4; suit += 1) deck.push(...CARD_RANKS);
  for (let i = deck.length - 1; i > 0; i -= 1) {
    const j = rng.int(0, i);
    const a = deck[i] as Card;
    deck[i] = deck[j] as Card;
    deck[j] = a;
  }
  return deck;
}

function draw(deck: readonly Card[]): { card: Card; deck: Card[] } {
  if (deck.length === 0) throw new RangeError('blackjack: deck is empty');
  return { card: deck[0] as Card, deck: deck.slice(1) };
}

/** Дилер берёт до 17 включительно. */
export function dealerPlay(dealer: readonly Card[], deck: readonly Card[]): { dealer: Card[]; deck: Card[] } {
  let hand = [...dealer];
  let rest = [...deck];
  while (handValue(hand) < BLACKJACK_DEALER_STANDS) {
    const d = draw(rest);
    hand = [...hand, d.card];
    rest = d.deck;
  }
  return { dealer: hand, deck: rest };
}

/** Игрок берёт одну карту. */
export function blackjackHit(state: BlackjackState): BlackjackState {
  const d = draw(state.deck);
  return { ...state, hand: [...state.hand, d.card], deck: d.deck };
}

export function isBust(hand: readonly Card[]): boolean {
  return handValue(hand) > 21;
}

function cardValue(card: Card): number {
  if (card === 'A') return 11;
  if (card === 'K' || card === 'Q' || card === 'J' || card === '10') return 10;
  return Number(card);
}

/** Мягкая рука: туз считается за 11 без перебора. */
export function isSoft(hand: readonly Card[]): boolean {
  let hard = 0;
  let aces = 0;
  for (const c of hand) {
    if (c === 'A') aces += 1;
    hard += c === 'A' ? 1 : cardValue(c);
  }
  return aces > 0 && hard + 10 <= 21;
}

/** Наивная стратегия: брать, пока меньше 17 (как дилер). */
export function blackjackNaiveAction(hand: readonly Card[]): 'hit' | 'stand' {
  return handValue(hand) < BLACKJACK_DEALER_STANDS ? 'hit' : 'stand';
}

/**
 * Базовая стратегия без удвоений и сплитов (подсказка после BLACKJACK_LEARN_HANDS рук):
 * жёсткие 17+ — стоять; 13–16 — стоять против 2–6, брать против 7–A;
 * 12 — стоять против 4–6, иначе брать; 11 и меньше — брать;
 * мягкие 19+ — стоять; мягкие 18 — стоять против 2–8, брать против 9, 10, A;
 * мягкие 17 и меньше — брать.
 */
export function blackjackBasicAction(hand: readonly Card[], dealerUp: Card): 'hit' | 'stand' {
  const total = handValue(hand);
  const up = cardValue(dealerUp);
  if (isSoft(hand)) {
    if (total >= 19) return 'stand';
    if (total === 18) return up >= 2 && up <= 8 ? 'stand' : 'hit';
    return 'hit';
  }
  if (total >= 17) return 'stand';
  if (total >= 13) return up <= 6 ? 'stand' : 'hit';
  if (total === 12) return up >= 4 && up <= 6 ? 'stand' : 'hit';
  return 'hit';
}

function settle(state: BlackjackState): { mult: number; logKey: string; dealer: Card[] } {
  if (isBust(state.hand)) return { mult: 0, logKey: 'minigame.blackjack.lose', dealer: state.dealer };
  const played = dealerPlay(state.dealer, state.deck);
  const player = handValue(state.hand);
  const dealer = handValue(played.dealer);
  if (isNatural(state.hand) && dealer !== 21) return { mult: BLACKJACK_NATURAL_PAYOUT, logKey: 'minigame.blackjack.natural', dealer: played.dealer };
  if (dealer > 21 || player > dealer) return { mult: 2, logKey: 'minigame.blackjack.win', dealer: played.dealer };
  if (player === dealer) return { mult: 1, logKey: 'minigame.blackjack.push', dealer: played.dealer };
  return { mult: 0, logKey: 'minigame.blackjack.lose', dealer: played.dealer };
}

function handsOf(life: Life): number {
  const raw = life.flags[FLAG_BJ_HANDS];
  return typeof raw === 'number' && raw > 0 ? Math.trunc(raw) : 0;
}

export const blackjack: MinigameDef<BlackjackInput, BlackjackState> = {
  id: 'blackjack',
  section: 'E',
  triggers: [{ kind: 'envelope' }],
  available(life: Life, _ds: Dataset, law: GamblingLaw): boolean {
    return casinoAvailable(life, law);
  },
  setup(life: Life, _ds: Dataset, rng: Rng): BlackjackState {
    const deck = shuffledDeck(rng);
    return { hand: [deck[0]!, deck[2]!], dealer: [deck[1]!, deck[3]!], deck: deck.slice(4), maxBet: Math.max(0, life.money), hands: handsOf(life) };
  },
  play(state: BlackjackState, input: BlackjackInput, life: Life): MinigameOutcome {
    const bet = clampBet(Math.min(input.bet, state.maxBet), life.money);
    if (bet <= 0) return noPlay();
    const current = input.action === 'hit' ? blackjackHit(state) : state;
    const result = settle(current);
    const moneyDelta = bet * result.mult - bet;
    const learning = state.hands < BLACKJACK_LEARN_HANDS;
    const outcome: MinigameOutcome = {
      moneyDelta,
      urgeDelta: BLACKJACK_URGE,
      flags: { [FLAG_BJ_HANDS]: state.hands + 1 },
      logKey: result.logKey,
      logParams: { amount: Math.round(Math.abs(moneyDelta)), player: handValue(current.hand), dealer: handValue(result.dealer) },
    };
    if (learning) outcome.stats = { knowledge: 1 };
    return outcome;
  },
  expected(): MinigameOutcome {
    return noPlay();
  },
  expectancyText: (state: BlackjackState) => expectancy('blackjack', state.hands >= BLACKJACK_LEARN_HANDS ? BLACKJACK_RTP_BASIC : BLACKJACK_RTP_NAIVE),
};
